// ── ContractorMarketplacePanel ──
// Read-only eligibility breakdown + admin override controls.
// P0 surface only — no offer creation here.

import { useState, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, XCircle, ShieldCheck, Store, AlertTriangle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import {
  resolveContractorEligibility,
  type EligibilityFactor,
} from "@/data/contractor/marketplaceEligibility";
import type { Contractor } from "@/data/types";
import { ContractorService } from "@/services/contractor.service";

const ADMIN_ROLES = new Set(["admin", "super_admin", "owner"]);

interface Props {
  contractor: Contractor;
}

function FactorRow({ factor }: { factor: EligibilityFactor }) {
  const Icon = factor.passed ? CheckCircle2 : factor.blocking ? XCircle : AlertTriangle;
  const tone = factor.passed
    ? "text-success"
    : factor.blocking
      ? "text-destructive"
      : "text-warning";
  return (
    <div className="flex items-start gap-2 py-1.5">
      <Icon className={`h-4 w-4 mt-0.5 ${tone}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium">{factor.label}</span>
          {!factor.blocking && (
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">soft</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{factor.reason}</p>
      </div>
    </div>
  );
}

export function ContractorMarketplacePanel({ contractor }: Props) {
  const { contractors, payProfiles, setContractors } = useAppData();
  const onboarding = useOnboarding();
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const isAdmin = hasPerm("contractors_edit");
  const parentContractor = contractor.parentEntityId
    ? contractors.find((item) => item.id === contractor.parentEntityId)
    : undefined;

  const eligibility = useMemo(
    () =>
      resolveContractorEligibility(contractor, {
        payProfiles,
        complianceDocuments: onboarding.complianceDocuments,
        onboardingSessions: onboarding.sessions,
        parentContractor,
      }),
    [
      contractor,
      onboarding.complianceDocuments,
      onboarding.sessions,
      parentContractor,
      payProfiles,
    ],
  );

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [decision, setDecision] = useState<"allow" | "deny">("allow");
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");

  const optInLabel = contractor.marketplaceOptIn ? "Opted in" : "Not opted in";

  const persistOverride = useCallback(
    async (next: Contractor["marketplaceAdminOverride"]) => {
      const { error } = await ContractorService.updateMarketplaceAdminOverride(contractor.id, next);
      if (error) {
        toast({ title: "Failed to save override.", description: error, variant: "destructive" });
        return false;
      }
      return true;
    },
    [contractor.id],
  );

  const handleOverrideApply = async () => {
    if (confirmText !== "CONFIRM") {
      toast({ title: "Type CONFIRM to apply override.", variant: "destructive" });
      return;
    }
    if (reason.trim().length < 8) {
      toast({ title: "Reason must be at least 8 characters.", variant: "destructive" });
      return;
    }
    const next = {
      decision,
      reason: reason.trim(),
      setBy: role,
      setAt: new Date().toISOString(),
    };
    const ok = await persistOverride(next);
    if (!ok) return;
    setContractors((prev) =>
      prev.map((c) => (c.id === contractor.id ? { ...c, marketplaceAdminOverride: next } : c)),
    );
    toast({ title: `Override applied: ${decision}.` });
    setOverrideOpen(false);
    setReason("");
    setConfirmText("");
  };

  const handleOverrideClear = async () => {
    const ok = await persistOverride(null);
    if (!ok) return;
    setContractors((prev) =>
      prev.map((c) => (c.id === contractor.id ? { ...c, marketplaceAdminOverride: null } : c)),
    );
    toast({ title: "Override cleared." });
  };

  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
            <Store className="h-4 w-4" /> Marketplace
          </h2>
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full font-medium ring-1 ${
              eligibility.eligible
                ? "bg-success/10 text-success ring-success/20"
                : "bg-muted text-muted-foreground ring-border"
            }`}
          >
            {eligibility.eligible ? "Eligible" : "Ineligible"}
            {eligibility.overrideActive && " · Override"}
          </span>
        </div>

        <div className="text-xs text-muted-foreground mb-2">{optInLabel}</div>

        <div className="border-t pt-2">
          {eligibility.factors.map((f) => (
            <FactorRow key={f.key} factor={f} />
          ))}
        </div>

        {isAdmin && (
          <div className="mt-3 pt-3 border-t flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setOverrideOpen(true)}>
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
              {contractor.marketplaceAdminOverride ? "Update override" : "Set override"}
            </Button>
            {contractor.marketplaceAdminOverride && (
              <Button size="sm" variant="ghost" onClick={handleOverrideClear}>
                Clear
              </Button>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marketplace eligibility override</DialogTitle>
            <DialogDescription>
              Force-allow or force-deny this contractor in the marketplace. Audited.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex gap-2">
              {(["allow", "deny"] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDecision(d)}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium ring-1 transition ${
                    decision === d
                      ? "bg-primary/15 text-primary ring-primary/30"
                      : "bg-muted/40 text-muted-foreground ring-border hover:bg-muted"
                  }`}
                >
                  {d === "allow" ? "Allow" : "Deny"}
                </button>
              ))}
            </div>

            <div>
              <Label className="text-xs">Reason</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this override necessary?"
                rows={3}
              />
            </div>

            <div>
              <Label className="text-xs">Type CONFIRM to apply</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CONFIRM"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverrideOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleOverrideApply}>Apply override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
